import React, { useEffect, useState, useMemo } from "react";
import { ResponsiveChoropleth } from "@nivo/geo";
import { fetchVisitorsByCountryGA } from "../services/api";
import worldCountries from "../data/world_countries.json";
import isoCountries from "i18n-iso-countries";
import Spinner from "./Spinner";
isoCountries.registerLocale(require("i18n-iso-countries/langs/en.json"));

/**
 * VisitorsByCountryMap – choropleth that replicates the Looker Studio geo‑chart
 * (dimension = Country, metric = Total Users).
 * Date range: First day to last day of the month from startDate.
 */
const VisitorsByCountryMap = ({ startDate }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchAndTransform = async () => {
      setIsLoading(true);
      try {
        // Extract year and month from startDate prop (e.g., "2025-09-02")
        // gaStart = first day of that month (e.g., "2025-09-01")
        // gaEnd = last day of that month (e.g., "2025-09-30")
        const startOfMonth = new Date(`${startDate}T00:00:00`);
        const monthStart = new Date(startOfMonth.getFullYear(), startOfMonth.getMonth(), 1);
        const monthEnd = new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 1, 0);
        const gaStart = monthStart.toISOString().slice(0, 10);
        const gaEnd = monthEnd.toISOString().slice(0, 10);

        const raw = await fetchVisitorsByCountryGA(gaStart, gaEnd);
        
        // Data is already aggregated by country from backend
        // Just translate country names to ISO-3 codes
        const chartData = raw
          .map(({ country, totalUsers }) => {
            const iso3 = isoCountries.getAlpha3Code(country.trim(), "en");
            if (!iso3) return null; // unknown country – skip
            
            return {
              id: iso3,
              value: Number(totalUsers || 0),
            };
          })
          .filter(item => item !== null);

        console.log("chartData", chartData);
        setData(chartData);
      } catch (err) {
        console.error("Failed to load visitors‑by‑country data", err);
      } finally {
        setIsLoading(false);
      }
    };

    setData([]);
    fetchAndTransform();
  }, [startDate]);

  /**
   * Keep the color domain in sync with changing data – memoised for perf.
   */
  const maxValue = useMemo(() => (data.length ? Math.max(...data.map((d) => d.value)) : 0), [data]);

  return (
    <div style={{ height: 500, width: "100%", maxWidth: 900 }}>
      {isLoading ? (
        <Spinner label="Loading map…" />
      ) : data.length ? (
        <ResponsiveChoropleth
          data={data}
          features={worldCountries.features}
          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
          colors="blues"
          domain={[0, maxValue]}
          unknownColor="#f0f0f0"
          label="properties.name"
          valueFormat=".0f"
          projectionScale={120}
          projectionTranslation={[0.5, 0.55]}
          borderWidth={0.7}
          borderColor={{ theme: "background" }}
          
          onClick={(feature) => {
            
            console.log("Clicked country", feature);
          }}
          legends={[
            {
              anchor: "bottom-left",
              direction: "row",
              justify: true,
              translateY: 36,
              itemsSpacing: 0,
              itemWidth: 60,
              itemHeight: 14,
              itemDirection: "left-to-right",
              itemTextColor: "#555",
              symbolSize: 14,
            },
          ]}
        />
      ) : (
        <p>No data available.</p>
      )}
    </div>
  );
};

export default VisitorsByCountryMap;
